import { UserSoundPortalLoadError } from "./errors.js";
import type { PortalLoadErrorReason } from "./protocol.js";

/**
 * Validates that the portal URL returns a successful (2xx) response.
 * Uses a GET request to match what the iframe will do; some servers return
 * 200 for HEAD but 404 for GET, which would show the browser's 404 in the iframe.
 *
 * @param uri - The portal URL to validate
 * @throws {UserSoundPortalLoadError} When the URL returns non-2xx, or on network/CORS failure
 */
export async function validatePortalUrl(uri: string): Promise<void> {
  try {
    const response = await fetch(uri, {
      method: "GET",
      mode: "cors",
      cache: "no-store",
    });
    if (!response.ok) {
      throw new UserSoundPortalLoadError(
        `Portal failed to load: ${response.status} ${response.statusText}`,
        uri,
        "http_error",
        response.status
      );
    }
  } catch (err) {
    if (err instanceof UserSoundPortalLoadError) throw err;
    const message =
      err instanceof Error ? err.message : "Failed to load survey";
    let reason: PortalLoadErrorReason = "network_error";
    if (err instanceof TypeError) {
      const lower = err.message.toLowerCase();
      if (
        lower.includes("cors") ||
        lower.includes("access-control-allow-origin")
      ) {
        reason = "cors";
      }
    }
    throw new UserSoundPortalLoadError(message, uri, reason, undefined, err);
  }
}
